import { useForm } from '@mantine/form'
import { useDebouncedCallback } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { useMutation } from '@tanstack/react-query'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { useEffect, useMemo, useReducer, useRef } from 'react'
import { z } from 'zod'

import { useGlobalState } from '@/hooks/useGlobalState'
import type {
  CreateFAQsParams,
  GenerateDemographicResponse,
  GenerateMetadataResponse,
  GenerateObjectivesResponse,
  GetContentModuleResponse,
  GetFAQsResponse,
  GetStepsResponse,
  UpdateContentModuleParams,
  UpdateFAQsParams,
  UpdateStepParams,
} from '@/services/api/api.types'

import { load, remove, save } from './storage'

/**
 *
 * Schemas
 *
 */
export const contentSchema = z.object({
  // contentId: z.nullable(z.string()), // not needed for now
  file: z.nullable(z.instanceof(File)),
  text: z.string().min(1, "The content can't be blank."), // content: for GET; text: for POST
})

/**
 * Per-CM media-mode opt-in marker in delivery-style templates. Drives the
 * `is_media_mode` save flag in `castZodFormToContentModule`. Editor UI mode
 * is channel-level only — see `useEffectiveMediaMode`.
 */
export const MEDIA_MODE_DELIVERY_TEMPLATE_MARKER = 'RELATED FILE'

/**
 * Splits a steps list into primary (index 0) and supplementary. Single place
 * to update if the BE ever moves to an explicit `stepRole`/`isPrimary` field.
 */
export function splitMediaSteps<T>(steps: T[] | null | undefined): { primary?: T; supplementary: T[] } {
  if (!steps || steps.length === 0) return { supplementary: [] }
  const [primary, ...supplementary] = steps
  return { primary, supplementary }
}

const GENERIC_PROCESS_ERROR =
  'There was an issue processing this file. This may be due to the file being too large or in an unsupported format.'

// Maps terse backend processing errors to user-friendly copy.
export function getProcessErrorMessage(raw?: string | null): string {
  if (!raw) return GENERIC_PROCESS_ERROR
  if (/failed to scrape url/i.test(raw)) {
    return "We couldn't read the content from this URL. The site may block automated access or be temporarily unavailable. Please delete this file and add a different URL or upload the content directly."
  }
  return GENERIC_PROCESS_ERROR
}

// Required-ness is layered via `superRefine` so the inferred type stays the
// same across modes.
export const moduleObjectSchema = z
  .object({
    taskId: z.nullable(z.string()),
    name: z.string().min(1, 'Module name is required.'),
    shortDescription: z.string().min(1, 'Introduction is required.'),
    presentationMode: z.enum(['sequential', 'member_directed']),
    access: z.enum(['restricted', 'open']),
    expirationDate: z.date().optional(),
    dueDate: z.date().optional(),
    categories: z.array(z.string()),
    mappings: z.array(z.string()).optional(),
    selectEsk: z.string().min(1, 'Sidekick selection is required.'),
    content: contentSchema,
    demographic: z.string(),
    priority: z.number().optional(),
    sectionObjectives: z.string().optional(),
    titleImage: z.nullable(z.string()),
    introduction: z.string(),
    personality: z.string().optional(),
    presentationObjectives: z.string().optional(),
    outline: z.string(),
    deliveryStyle: z.string(),
    visibleToMsk: z.boolean(),
    allowedForAllSponsors: z.boolean(),
  })
  .partial({ expirationDate: true, dueDate: true })

export const createModuleSchema = (isMediaMode = false) =>
  moduleObjectSchema.superRefine((data, ctx) => {
    if (isMediaMode) return

    if (!data.introduction || data.introduction.trim().length === 0) {
      ctx.addIssue({ code: 'custom', message: 'Introduction is required.', path: ['introduction'] })
    }

    if (!data.outline || data.outline.trim().length === 0) {
      ctx.addIssue({ code: 'custom', message: 'Outline is required', path: ['outline'] })
    }

    if (!data.deliveryStyle || data.deliveryStyle.trim().length === 0) {
      ctx.addIssue({ code: 'custom', message: 'Delivery Style is required', path: ['deliveryStyle'] })
    }
  })

export const moduleSchema = createModuleSchema(false)

/**
 * Required module-form fields per mode. Shared between `ActionsSection`
 * (submit) and `ContentModuleFormScreen.sectionValidation` (per-section
 * validity dots) so they can't drift apart.
 */
export type ModuleFormFieldName = keyof z.infer<typeof moduleObjectSchema>

export function getRequiredModuleFieldsByMode(isMediaMode: boolean): {
  topicSettings: ModuleFormFieldName[]
  requiredSettings: ModuleFormFieldName[]
  customizeDelivery: ModuleFormFieldName[]
  presentation: ModuleFormFieldName[]
  all: ModuleFormFieldName[]
} {
  const topicSettings: ModuleFormFieldName[] = ['selectEsk']
  const presentation: ModuleFormFieldName[] = ['presentationMode']
  const requiredSettings: ModuleFormFieldName[] = isMediaMode
    ? ['name', 'shortDescription']
    : ['name', 'shortDescription', 'introduction']
  const customizeDelivery: ModuleFormFieldName[] = isMediaMode ? [] : ['outline', 'deliveryStyle']

  return {
    topicSettings,
    requiredSettings,
    customizeDelivery,
    presentation,
    all: [...topicSettings, ...presentation, ...requiredSettings, ...customizeDelivery],
  }
}

export const faqSchema = z.object({
  faqId: z.nullable(z.string()),
  question: z.string().min(1, 'Question is required.'),
  answer: z.string().min(1, "Answer can't be empty."),
})

export const stepSchema = z.object({
  stepId: z.nullable(z.string()),
  content: contentSchema,
  name: z.string().min(1, 'Title is required.'),
  titleImage: z.nullable(z.string()),
  sectionObjectives: z.string().min(1, 'Section objectives are required.'),
  introduction: z.nullable(z.string()), // esk introduction
  shortDescription: z.string().min(1, 'Short description is required.'), // esk reminder
  title: z.nullable(z.string()), // msk reminder
  // deliveryMode: z.nativeEnum(DeliveryMode),
  // access: z.enum(['restricted', 'open']), // privacy setting - NOT IMPLEMENTED
})

/**
 *
 * Form Hooks
 *
 */
export function useContentModuleForm(opts?: { initialData?: GetContentModuleResponse; isMediaMode?: boolean }) {
  const shouldCache = useRef(false)

  const { api, uiStore } = useGlobalState()

  const cmChangeStatusMutation = useMutation({
    mutationFn: api.changeContentModuleStatus,
    onSuccess: (d, _vars) => {
      const success = 'status' in d && d.status
      const error = 'errors' in d

      if (success) {
        api.queryClient.invalidateQueries({
          queryKey: ['cm', 'list'],
        })
      } else if (error) {
        console.error('Error updating module status', d)
      }
    },
    onError: error => {
      console.error('Error updating module status', error)
    },
  })

  const debouncedSaveToCache = useDebouncedCallback(() => {
    saveToCache()
  }, 500)

  // Debounced counter for parent sectionValidation to recompute without per-keystroke renders.
  const [valuesVersion, bumpValuesVersion] = useReducer((c: number) => c + 1, 0)
  const debouncedBumpVersion = useDebouncedCallback(bumpValuesVersion, 600)

  const activeSchema = useMemo(() => createModuleSchema(opts?.isMediaMode ?? false), [opts?.isMediaMode])

  // Mantine's useForm captures `validate` at init; ref indirection lets us swap the
  // resolver when isMediaMode resolves post-mount (sponsor-info query landing late).
  const resolverRef = useRef(zod4Resolver(activeSchema))
  useEffect(() => {
    resolverRef.current = zod4Resolver(activeSchema)
  }, [activeSchema])

  const form = useForm<z.infer<ModuleFormSchema>>({
    mode: 'uncontrolled',
    validate: (values, ...rest) => resolverRef.current(values, ...(rest as [])),
    validateInputOnBlur: true,
    initialValues: castContentModuleToZodForm(opts?.initialData),
    onValuesChange() {
      debouncedSaveToCache()
      debouncedBumpVersion()
    },
  })

  function loadFromCache() {
    const values = form.getValues()

    if (values.taskId) {
      return load(`@sidekick/forms/cm/${values.taskId}`)
    } else {
      return null
    }
  }

  function saveToCache() {
    const values = form.getValues()

    if (shouldCache.current && values.taskId) {
      save(`@sidekick/forms/cm/${values.taskId}`, values)
    }
  }

  function clearCache() {
    const taskId = form.getValues().taskId

    if (taskId) {
      remove(`@sidekick/forms/cm/${taskId}`)
    }
  }

  // Only re-run the navigation blocker effect when dirty/touched actually changes.
  const prevDirtyTouchedRef = useRef({ dirty: false, touched: false })

  useEffect(() => {
    const isDirty = form.isDirty()
    const isTouched = form.isTouched()
    const prev = prevDirtyTouchedRef.current
    const isNewCM = uiStore.isNewContentModule

    if (prev.dirty === isDirty && prev.touched === isTouched && !isNewCM) {
      return
    }
    prevDirtyTouchedRef.current = { dirty: isDirty, touched: isTouched }

    const deleteNewCM = () => {
      if (isNewCM) {
        uiStore.setIsNewContentModule(false)
        // Delete the new content module from the database
        const taskId = form.getValues().taskId
        if (taskId) {
          cmChangeStatusMutation.mutate({
            task_id: taskId,
            status: 'removed',
          })
        }
      }
    }

    if (isDirty && isTouched) {
      shouldCache.current = true
      saveToCache()

      uiStore.setRouterLocationChangeFn(async () => {
        let resolveFn: ((v: boolean) => void) | undefined

        const confirmation = new Promise<boolean>(resolve => (resolveFn = resolve))

        modals.openConfirmModal({
          title: 'Unsaved Changes',
          centered: true,
          children: `You have unsaved changes. Are you sure you want to discard them?`,
          labels: { confirm: 'Discard Changes', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          groupProps: { mt: 'xl' },
          onConfirm: () => {
            clearCache()
            resolveFn?.(true)
            deleteNewCM()
          },
          onCancel: () => resolveFn?.(false),
        })

        const confirmed = await confirmation

        return confirmed
      })
    } else if (isNewCM) {
      uiStore.setRouterLocationChangeFn(async () => {
        let resolveFn: ((v: boolean) => void) | undefined

        const confirmation = new Promise<boolean>(resolve => (resolveFn = resolve))

        modals.openConfirmModal({
          title: 'Unsaved New Content Module',
          centered: true,
          children: `Leaving this page will discard your new content module. Are you sure you want to continue?`,
          labels: { confirm: 'Discard Content Module', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          groupProps: { mt: 'xl' },
          onConfirm: () => {
            clearCache()
            resolveFn?.(true)
            deleteNewCM()
          },
          onCancel: () => resolveFn?.(false),
        })

        const confirmed = await confirmation

        return confirmed
      })
    }

    return () => {
      uiStore.setRouterLocationChangeFn(undefined)
    }
  }, [form.isDirty(), form.isTouched()])

  useEffect(() => {
    if (form.initialized) {
      const cached = loadFromCache()

      if (cached) {
        modals.openConfirmModal({
          title: 'Restore Unsaved Changes',
          centered: true,
          children: `There are unsaved changes from your previous session. Would you like to restore them?`,
          labels: { confirm: 'Restore Changes', cancel: 'Cancel' },
          groupProps: { mt: 'xl' },
          onConfirm: () => {
            form.setValues(cached)
            form.setTouched({ taskId: true })
          },
          onCancel: () => clearCache(),
        })
      }
    }
  }, [form.initialized])

  return { form, schema: activeSchema, contentSchema, valuesVersion }
}

export function useFAQForm() {
  const form = useForm<z.infer<FAQFormSchema>>({
    mode: 'controlled',
    validate: zod4Resolver(faqSchema),
    validateInputOnBlur: true,
    initialValues: castFAQToZodForm(),
  })

  return { form, schema: faqSchema }
}

export function useStepForm() {
  const form = useForm<z.infer<StepFormSchema>>({
    mode: 'controlled',
    validate: zod4Resolver(stepSchema),
    validateInputOnBlur: true,
    initialValues: castStepToZodForm(),
  })

  return { form, schema: stepSchema }
}

export function useMetadataForm() {
  return useForm<MetadataFormSchema>({
    mode: 'controlled',
  })
}

/**
 *
 * Data Transformation
 *
 */
export function castContentModuleToZodForm(data?: Partial<GetContentModuleResponse>): z.infer<ModuleFormSchema> {
  return {
    taskId: data?.taskId ?? null,
    name: data?.name ?? '',
    shortDescription: data?.shortDescription ?? '',
    presentationMode: data?.areStepSequential ? 'sequential' : 'member_directed',
    selectEsk: data?.selectEsk ?? 'PSK',
    categories: data?.categories ?? [],
    mappings: data?.mappings ?? [],
    content: { text: '', file: null },
    demographic: data?.demographic ?? '',
    priority: data?.priority ?? undefined,
    visibleToMsk: false,
    allowedForAllSponsors: data?.allowedForAllSponsors ?? false,
    sectionObjectives: data?.sectionObjectives ?? '',
    titleImage: data?.titleImage ?? null,
    introduction: data?.introduction ?? '',
    personality: data?.presentationPersonality ?? '',
    presentationObjectives: data?.presentationObjectives ?? '',
    outline: data?.outline ?? '',
    deliveryStyle: data?.presentationStyle ?? '',
    // mock
    access: 'open',
  }
}

export function castFAQToZodForm(data?: Partial<GetFAQsResponse['0']>): z.infer<FAQFormSchema> {
  return {
    faqId: data?.faqId ?? null,
    question: data?.question ?? '',
    answer: data?.answer ?? '',
  }
}

export function castZodFormToContentModule(
  data: z.infer<ModuleFormSchema>,
  opts: { isMediaMode: boolean },
): Omit<UpdateContentModuleParams, 'task_id'> {
  // Must be explicit `false`, not null: backend strips null before $set, so
  // null can never turn media mode back off.
  const is_media_mode = opts.isMediaMode
    ? true
    : data.deliveryStyle?.includes(MEDIA_MODE_DELIVERY_TEMPLATE_MARKER)
      ? true
      : false

  return {
    name: data.name,
    title: data.name,
    short_description: data.shortDescription,
    are_step_sequential: data.presentationMode === undefined ? undefined : data.presentationMode === 'sequential',
    select_esk: data.selectEsk,
    categories: data.categories,
    mappings: data.mappings,
    long_description: 'NOT_IMPLEMENTED_FRONT_END',
    demographic: data.demographic,
    visible_to_msk: false,
    allowed_for_all_sponsors: data.allowedForAllSponsors,
    title_image: data.titleImage,
    introduction: data.introduction,
    section_objectives: data.sectionObjectives || null, // Include section objectives when saving
    presentation_personality: data.personality,
    presentation_objectives: data.presentationObjectives,
    presentation_style: data.deliveryStyle,
    is_media_mode,
    outline: data.outline,
  }
}

export function castZodFormToFAQsUpdate(data: z.infer<FAQFormSchema>): Omit<UpdateFAQsParams, 'task_id' | 'step_id'> {
  return {
    faqs: [
      {
        question: data.question,
        answer: data.answer,
        faq_id: data.faqId!,
      },
    ],
  }
}

export function castZodFormToFAQsCreate(data: z.infer<FAQFormSchema>): Omit<CreateFAQsParams, 'task_id' | 'step_id'> {
  return {
    faqs: [
      {
        question: data.question,
        answer: data.answer,
      },
    ],
  }
}

export function castStepToZodForm(data?: Partial<GetStepsResponse[0]>): z.infer<StepFormSchema> {
  return {
    stepId: data?.stepId ?? null,
    content: { text: '', file: null },
    name: data?.name ?? '',
    titleImage: data?.titleImage ?? '',
    introduction: data?.introduction ?? '',
    shortDescription: data?.shortDescription ?? '',
    title: data?.title ?? '',
    // deliveryMode: data?.deliveryMode ?? stepSchema.shape.deliveryMode.enum.Paraphrase,
    sectionObjectives: data?.sectionObjectives ?? '',
    // mock
    // access: 'open',
  }
}

export function castZodFormToStep(data: z.infer<StepFormSchema>): Omit<UpdateStepParams, 'step_id'> {
  return {
    name: data.name,
    title_image: data.titleImage ?? '',
    introduction: data.introduction ?? '',
    short_description: data.shortDescription ?? '',
    title: data.name ?? '',
    section_objectives: data.sectionObjectives,
    // delivery_mode: data.deliveryMode,
  }
}

export type ModuleFormSchema = typeof moduleSchema
export type FAQFormSchema = typeof faqSchema
export type ContentFormSchema = typeof contentSchema
export type StepFormSchema = typeof stepSchema
export type MetadataFormSchema = {
  module?: GenerateMetadataResponse['generated'][number]
  step?: GenerateMetadataResponse['generated'][number]
  demographic?: GenerateDemographicResponse['demographic']
  sectionObjectives?: GenerateObjectivesResponse['objectives'] // deprecated
}
